import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader, StatCard } from "@/components/ui/Card";
import { EmptyState } from "@/components/ui/EmptyState";
import { Spinner } from "@/components/ui/Spinner";
import { Table, Td, Th } from "@/components/ui/Table";
import { useAuth } from "@/hooks/useAuth";
import { useEvents } from "@/hooks/useEvents";
import { teams as teamsApi } from "@/lib/api";
import { strings } from "@/lib/strings";
import type { TeamDashboard } from "@/lib/types";

/** Team dashboard (docs/08 §8.5, M-07, F-05). */
export function Dashboard() {
  const { isElevated } = useAuth();
  const [board, setBoard] = useState<TeamDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [stale, setStale] = useState(false);
  const [allTeams, setAllTeams] = useState(isElevated);

  const load = useCallback(async () => {
    try {
      setBoard(await teamsApi.myDashboard(7, allTeams));
      setStale(false);
    } catch {
      // docs/08 §8.5: offline shows the last snapshot with a banner.
      setStale(true);
    } finally {
      setLoading(false);
    }
  }, [allTeams]);

  useEffect(() => {
    void load();
  }, [load]);

  // Keep the counts live as entries land.
  useEvents(useCallback(() => void load(), [load]));

  if (loading) return <Spinner label="Loading the dashboard" />;
  if (!board) return <EmptyState title="No data yet." />;

  const maxPerDay = Math.max(1, ...Object.values(board.per_day));

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-xl font-semibold text-slate-900">
          {strings.dashboard.title}
          {board.team ? ` · ${board.team.name}` : ""}
        </h1>
        {isElevated && (
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={allTeams}
              onChange={(event) => setAllTeams(event.target.checked)}
              className="h-4 w-4 rounded border-slate-300"
            />
            Show every team
          </label>
        )}
      </div>

      {stale && (
        <p className="rounded-md bg-amber-50 p-3 text-sm text-amber-900">
          {strings.dashboard.staleNotice}
        </p>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <StatCard label={strings.dashboard.totalIsolates} value={board.total} />
        <StatCard label={strings.dashboard.complete} value={board.complete} tone="ok" />
        <StatCard
          label={strings.dashboard.needsReview}
          value={board.needs_review}
          tone={board.needs_review > 0 ? "warning" : "default"}
        />
        <StatCard label={strings.dashboard.voided} value={board.void} />
        <StatCard
          label={strings.dashboard.openFlags}
          value={board.open_flags}
          tone={board.open_blocking_flags > 0 ? "blocking" : "default"}
          hint={
            board.open_blocking_flags > 0
              ? `${board.open_blocking_flags} blocking, held out of the export`
              : undefined
          }
        />
      </div>

      {board.open_blocking_flags > 0 && (
        <p className="rounded-md bg-red-50 p-3 text-sm text-red-900">
          {board.open_blocking_flags} blocking flag
          {board.open_blocking_flags === 1 ? "" : "s"} must be resolved before those
          isolates can be exported.{" "}
          <Link to="/qa-flags" className="font-semibold underline">
            Open the QA queue
          </Link>
        </p>
      )}

      {/* A plain bar chart: no charting library for seven numbers. */}
      <Card>
        <CardHeader title={strings.dashboard.perDay} />
        <CardBody>
          <ol className="flex items-end gap-2" aria-label={strings.dashboard.perDay}>
            {Object.entries(board.per_day).map(([day, count]) => (
              <li key={day} className="flex flex-1 flex-col items-center gap-1">
                <span className="text-xs tabular-nums text-slate-600">{count}</span>
                <div
                  className="w-full rounded-t bg-brand-600"
                  style={{ height: `${Math.round((count / maxPerDay) * 96) + 4}px` }}
                  role="img"
                  aria-label={`${count} on ${day}`}
                />
                <span className="text-[10px] text-slate-500">{day.slice(5)}</span>
              </li>
            ))}
          </ol>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title={strings.dashboard.byVolunteer} />
        {board.volunteers.length === 0 ? (
          <EmptyState title="Nobody has entered anything yet." />
        ) : (
          <Table caption={strings.dashboard.byVolunteer}>
            <thead>
              <tr>
                <Th>Volunteer</Th>
                <Th className="text-right">Today</Th>
                <Th className="text-right">This week</Th>
                <Th className="text-right">Total</Th>
              </tr>
            </thead>
            <tbody>
              {board.volunteers.map((volunteer) => (
                <tr key={volunteer.user_id}>
                  <Td>{volunteer.full_name}</Td>
                  <Td className="text-right tabular-nums">{volunteer.today}</Td>
                  <Td className="text-right tabular-nums">{volunteer.this_week}</Td>
                  <Td className="text-right tabular-nums font-medium">{volunteer.total}</Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
    </div>
  );
}
